import { ChartBounds, ScatterPoint } from './types';

interface KeisukeScatterProps {
  bounds: ChartBounds;
  data: ScatterPoint[];
  xScale: (value: number) => number;
  yScale: (value: number) => number;
  pointRadius?: number;
  showRegressionLine?: boolean;
  regressionLineData?: Array<{ x: number; y: number }>;
  regressionLineColor?: string;
  symbolShape?: 'circle' | 'square' | 'triangle' | 'diamond' | 'cross' | 'plus' | 'star' | 'arrow-up' | 'arrow-down';
  symbolFillColor?: string;
  symbolStrokeColor?: string;
}

/**
 * KeisukeScatter - 散布図コンポーネント（回帰直線付き）
 */
export default function KeisukeScatter({
  bounds,
  data,
  xScale,
  yScale,
  pointRadius = 4,
  showRegressionLine = false,
  regressionLineData = [],
  regressionLineColor = '#d62728',
  symbolShape = 'circle',
  symbolFillColor,
  symbolStrokeColor = '#ffffff',
}: KeisukeScatterProps) {
  if (data.length === 0) return null;

  // Y軸のスケールを反転（SVGの座標系ではY軸が下向き）
  const yScaleReversed = (value: number) => bounds.height - yScale(value);

  // シンボルの形状をUnicode文字で取得
  const getSymbolUnicode = (shape: string): string => {
    switch (shape) {
      case 'circle':
        return '●';
      case 'square':
        return '■';
      case 'triangle':
        return '▲';
      case 'diamond':
        return '◆';
      case 'cross':
        return '✕';
      case 'plus':
        return '＋';
      case 'star':
        return '★';
      case 'arrow-up':
        return '↑';
      case 'arrow-down':
        return '↓';
      default:
        return '●';
    }
  };

  // シンボルの形状を描画する関数（Unicode文字を使用）
  const renderSymbol = (x: number, y: number, fillColor: string, strokeColor: string) => {
    const size = pointRadius;
    const fontSize = size * 2.5; // Unicode文字のサイズを調整
    const unicodeChar = getSymbolUnicode(symbolShape);
    
    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={symbolShape === 'cross' || symbolShape === 'plus' ? '0' : '0.3'}
        fontFamily="Arial, sans-serif"
      >
        {unicodeChar}
      </text>
    );
  };

  return (
    <g transform={`translate(${bounds.x}, ${bounds.y})`}>
      {/* 回帰直線 */}
      {showRegressionLine && regressionLineData.length > 1 && (
        <polyline
          points={regressionLineData
            .map((point) => `${xScale(point.x)},${yScaleReversed(point.y)}`)
            .join(' ')}
          fill="none"
          stroke={regressionLineColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* データポイント */}
      {data.map((point, index) => {
        const x = xScale(point.x);
        const y = yScaleReversed(point.y);
        const fillColor = symbolFillColor || point.color || '#1f77b4';
        const strokeColor = symbolStrokeColor || '#ffffff';
        
        return (
          <g key={index}>
            {renderSymbol(x, y, fillColor, strokeColor)}
          </g>
        );
      })}
    </g>
  );
}
